import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, X, PhoneCall, DollarSign, Mountain, Truck, Zap, MapPin, ArrowRight, ArrowLeft, Phone, ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
// FAQ entry. Only four short Q&As, so there's nothing to save by collapsing
// them — they read as plain copy, always open, with no card or accordion
// chrome competing with the answers.
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  return <div data-id="element-3019" data-mp="cl5yq082vhvr">
      <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-3020" data-mp="ckw6e5zk8ymw">
        {question}
      </h3>
      <p className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3021" data-mp="7mmd60xec83x">
        {answer}
      </p>
    </div>;
}
export function LocalVsNational() {
  const t = useT();
  // Carousel state for the "Why Local Matters" split panel (replaces the
  // old 6-card grid). Steps through the same benefits one at a time.
  const [whyIdx, setWhyIdx] = useState(0);
  const [whyDir, setWhyDir] = useState(1);
  const comparisonData = [{
    attribute: t('lvn.adv.1.attr'),
    local: t('lvn.adv.1.local'),
    national: t('lvn.adv.1.nat')
  }, {
    attribute: t('lvn.adv.2.attr'),
    local: t('lvn.adv.2.local'),
    national: t('lvn.adv.2.nat')
  }, {
    attribute: t('lvn.adv.3.attr'),
    local: t('lvn.adv.3.local'),
    national: t('lvn.adv.3.nat')
  }, {
    attribute: t('lvn.adv.4.attr'),
    local: t('lvn.adv.4.local'),
    national: t('lvn.adv.4.nat')
  }, {
    attribute: t('lvn.adv.5.attr'),
    local: t('lvn.adv.5.local'),
    national: t('lvn.adv.5.nat')
  }, {
    attribute: t('lvn.adv.6.attr'),
    local: t('lvn.adv.6.local'),
    national: t('lvn.adv.6.nat')
  }, {
    attribute: t('lvn.adv.7.attr'),
    local: t('lvn.adv.7.local'),
    national: t('lvn.adv.7.nat')
  }];
  const benefits = [{
    title: t('lvn.why.1.title'),
    desc: t('lvn.why.1.desc'),
    icon: PhoneCall
  }, {
    title: t('lvn.why.2.title'),
    desc: t('lvn.why.2.desc'),
    icon: DollarSign
  }, {
    title: t('lvn.why.3.title'),
    desc: t('lvn.why.3.desc'),
    icon: Mountain
  }, {
    title: t('lvn.why.4.title'),
    desc: t('lvn.why.4.desc'),
    icon: Truck
  }, {
    title: t('lvn.why.5.title'),
    desc: t('lvn.why.5.desc'),
    icon: Zap
  }, {
    title: t('lvn.why.6.title'),
    desc: t('lvn.why.6.desc'),
    icon: MapPin
  }];
  return <div className="bg-[#F2EFE8] dark:bg-[#222222] transition-colors duration-300" data-id="element-3022" data-mp="6uyexci2r3wv">
      {/* Hero Section — anchor target for "See how we stack up" links, since
          this section now lives partway down the About page. */}
      <section id="why-local" className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-3023" data-mp="3oc7wh8hbz55h">
        <div className="absolute inset-0 z-0" data-id="element-3024" data-mp="pm7oflruhsog">
          <img src="https://cdn.magicpatterns.com/uploads/eghevqpM2SdfKP6Caac4jm/file_000000008aac723095504e05a889ad89.png" alt="Dump truck delivery" className="w-full h-full object-cover" data-id="element-3025" data-mp="pmzn55gqo6iy" />
          <div className="absolute inset-0 bg-black/70 dark:bg-black/80" data-id="element-3026" data-mp="t56t05oap708" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3027" data-mp="x4yvq1r0cixo">
          <div className="max-w-3xl" data-id="element-3028" data-mp="1253annc9p5ls">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1.5 px-4 rounded-xl bg-brand-orange/20 text-brand-orange text-sm font-bold tracking-wider uppercase mb-6" data-id="element-3029" data-mp="12421xpiq4v78">
              {t('lvn.hero.eyebrow')}
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white mb-6 leading-tight" data-id="element-3030" data-mp="1kvl2lpp9zx63">
              {t('lvn.hero.title1')} <br data-id="element-3031" data-mp="1ztn8i6js4uqi" />
              <span className="text-[#03943D]" data-id="element-3032" data-mp="1zt3nggyu1q89">{t('lvn.hero.title2')}</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-stone-300 leading-relaxed mb-8 font-medium font-sub" data-id="element-3033" data-mp="2wiea7kdfq1v1">
              {t('lvn.hero.body')}
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-3034" data-mp="2dkuvc04app0p">
              <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-4 hover:bg-brand-orangeHover text-white rounded-xl font-bold text-base sm:text-lg transition-colors shadow-lg shadow-brand-orange/20 bg-[#03943D]" data-id="element-3035" data-mp="2mnciqhvosnpa">
                {t('lvn.hero.btnQuote')}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-3036" data-mp="3tyl3pj2g6zg5" />
              </Link>
              <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-xl font-bold text-base sm:text-lg transition-colors" data-id="element-3037" data-mp="5d87nkyf4f31">
                <Phone className="w-5 h-5 mr-2" data-id="element-3038" data-mp="3rmiugiwfd6s6" />
                {t('lvn.hero.btnCall')}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3039" data-mp="o6bsqhxkljm1">
        <div className="text-center mb-16" data-id="element-3040" data-mp="t2ak8ee73hqo">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-3041" data-mp="eepjus8pfwmg">
            {t('lvn.advantage.title')}
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-3042" data-mp="jawhzyuv03tu"></div>
          <p className="text-lg text-stone-600 dark:text-stone-300 max-w-2xl mx-auto font-sub" data-id="element-3043" data-mp="170qsi9rphgax">
            {t('lvn.advantage.subtitle')}
          </p>
        </div>

        {/* Unified comparison list — no table chrome, no pill-styled
            sub-labels. One row per attribute: small attribute label up top,
            then a two-column side-by-side answer (local | national) below.
            Works the same way on mobile and desktop. */}
        <div className="max-w-5xl mx-auto" data-id="element-3044" data-mp="17qar0q13pksx">
          {/* Compact column header — just two readable labels, no pills,
           no table TH. The "vs." mid-divider clarifies the relationship
           without dressing it up as table chrome. */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-6 items-baseline pb-4 mb-6 border-b border-stone-200 dark:border-white/10" data-id="element-3045" data-mp="xyn2oxzucjvo">
            <div data-id="element-3046" data-mp="ozt8fe9zg5la">
              <p className="text-[10px] sm:text-xs font-bold text-brand-orange uppercase tracking-[0.15em]" data-id="element-3047" data-mp="p0b160xfs1og">
                206Gravel
              </p>
              <p className="text-sm sm:text-base font-extrabold text-brand-charcoal dark:text-white leading-tight mt-0.5" data-id="element-3048" data-mp="k219w1eu1l84">
                {t('lvn.advantage.th2')}
              </p>
            </div>
            <span className="text-xs sm:text-sm font-bold text-stone-400 dark:text-stone-500 italic px-1 sm:px-3" data-id="element-3049" data-mp="2qqtip2h025hp">
              vs.
            </span>
            <div data-id="element-3050" data-mp="30ny43030twxg">
              <p className="text-[10px] sm:text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-[0.15em]" data-id="element-3051" data-mp="30nekzg1mx2hu">
                The Alternative
              </p>
              <p className="text-sm sm:text-base font-extrabold text-stone-500 dark:text-stone-400 leading-tight mt-0.5" data-id="element-3052" data-mp="27z0pb6y0qi9g">
                {t('lvn.advantage.th3')}
              </p>
            </div>
          </div>

          {/* Comparison rows. Each row: attribute label, then local vs.
           national answer side-by-side. Subtle row dividers, no nested
           card chrome. */}
          <div className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-3053" data-mp="1p89gc6hy0tvl">
            {comparisonData.map((row, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04,
            ease: 'easeOut'
          }} className="py-5 sm:py-6" data-id="element-3054" data-mp="1t804s3jzyxwy" data-mpi={idx}>
                {/* Attribute label */}
                <p className="text-[11px] sm:text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-[0.12em] mb-3" data-id="element-3055" data-mp="rjirdofwco0v">
                  {row.attribute}
                </p>
                {/* Side-by-side answers */}
                <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-6 items-start" data-id="element-3056" data-mp="1fa4zffpwi8av">
                  <div className="flex items-start gap-2 sm:gap-3" data-id="element-3057" data-mp="1abbifdbenz5y">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-3058" data-mp="1ac3f6rgqo2xt">
                      <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-600 dark:text-green-400" data-id="element-3059" data-mp="1k9r95f6it6ex" />
                    </div>
                    <span className="text-sm sm:text-base font-semibold text-brand-charcoal dark:text-white leading-snug" data-id="element-3060" data-mp="22yupjvkvs99y">
                      {row.local}
                    </span>
                  </div>
                  {/* Vertical divider — sits in the middle column on all sizes */}
                  <div className="w-px self-stretch bg-stone-200 dark:bg-white/10" aria-hidden="true" data-id="element-3061" data-mp="1yv56flmz6jwb" />
                  <div className="flex items-start gap-2 sm:gap-3 opacity-70" data-id="element-3062" data-mp="1k68zxt0khext">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center" data-id="element-3063" data-mp="1oyk2z4qltgul">
                      <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-stone-500 dark:text-stone-400" data-id="element-3064" data-mp="1b86tuz1z7w3o" />
                    </div>
                    <span className="text-sm sm:text-base font-medium text-stone-500 dark:text-stone-400 leading-snug" data-id="element-3065" data-mp="1f5uqc6jbhwxo">
                      {row.national}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pullquote Band */}
      <section className="py-24 bg-brand-charcoal dark:bg-black text-center px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-3066" data-mp="1oy0p29flapv7">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/pFhc3AjvRgqipTxvhW5pi7/file_000000003d4c720cb3b00791865cbb87.png')] bg-cover bg-center opacity-10 mix-blend-luminosity" data-id="element-3067" data-mp="1ayrkk70qxk72" />
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-3068" data-mp="23nq3v7glusdy">
          <Mountain className="w-12 h-12 text-brand-orange mx-auto mb-8 opacity-80" data-id="element-3069" data-mp="28l8gb7ttzpqp" />
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-relaxed mb-8" data-id="element-3070" data-mp="1twlow7qxj7j4">
            {t('lvn.quote')}
          </h2>
          <div className="w-24 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-3071" data-mp="1yudsw04umk3o"></div>
        </div>
      </section>

      {/* Why Local Matters */}
      <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-id="element-3072" data-mp="2mjg6mwwd3ssw">
        <div className="mb-8 max-w-2xl sm:mb-10" data-id="element-3073" data-mp="2rdaso6zfwjv5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#04A05C]" data-id="element-3074" data-mp="fw7h7vb5dzpb">Built for this area</p>
          <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-brand-charcoal dark:text-white sm:text-5xl" data-id="element-3075" data-mp="awuv5cs3owns">
            {t('lvn.why.title')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-stone-600 dark:text-stone-400 sm:text-lg" data-id="element-3076" data-mp="kn7dcycg0dqr">
            Local materials, local drivers, and people who know what your project needs before the truck leaves the yard.
          </p>
        </div>

        {(() => {
        const total = benefits.length;
        const active = benefits[whyIdx];
        const ActiveIcon = active.icon;
        const go = (dir: number) => {
          setWhyDir(dir);
          setWhyIdx(i => (i + dir + total) % total);
        };
        return <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.45,
          ease: 'easeOut'
        }} className="overflow-hidden rounded-2xl border border-stone-200 bg-[#fffcf9] shadow-sm dark:border-white/10 dark:bg-[#262626]" data-id="element-3077" data-mp="1hcyg1kuw177n">
              <div className="border-b border-stone-200 px-5 py-4 dark:border-white/10 sm:px-8" data-id="element-3078" data-mp="j2ymjx669xcv">
                <div className="flex items-center justify-between gap-4" data-id="element-3079" data-mp="o0gzdynb8wq5">
                  <div className="flex items-center gap-3" data-id="element-3080" data-mp="3nhewu0ti13mh">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#04A05C]/10 text-[#05894e] dark:bg-[#04A05C]/15 dark:text-[#4ed491]" data-id="element-3081" data-mp="3rhywbb1jojbs">
                      <ActiveIcon className="h-5 w-5" aria-hidden="true" data-id="element-3082" data-mp="9kzsfcm4x8c" />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 dark:text-stone-400" data-id="element-3083" data-mp="54h4aez1dd2c">Local advantage</p>
                  </div>
                  <span className="text-xs font-bold tabular-nums tracking-[0.16em] text-stone-400 dark:text-stone-500" data-id="element-3084" data-mp="38qfu36fch9at">
                    {String(whyIdx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                  </span>
                </div>
              </div>

              <div className="px-5 py-7 sm:px-8 sm:py-9" data-id="element-3085" data-mp="2r994ncz4uk48">
                <AnimatePresence mode="wait" data-id="element-3086" data-mp="2rbvczetul3ni">
                  <motion.div key={`why-panel-${whyIdx}`} initial={{
                opacity: 0,
                x: whyDir * 16
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: whyDir * -16
              }} transition={{
                duration: 0.25,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-3087" data-mp="2n8yo56dpsbb4">
                    <h3 className="max-w-3xl font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-brand-charcoal dark:text-white sm:text-4xl" data-id="element-3088" data-mp="23l98tymys3is">
                      {active.title}
                    </h3>
                    <p className="mt-4 max-w-3xl text-base leading-relaxed text-stone-600 dark:text-stone-300 sm:text-lg" data-id="element-3089" data-mp="1ukbanwz7iq84">
                      {active.desc}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="flex items-center justify-between gap-5 border-t border-stone-200 px-5 py-4 dark:border-white/10 sm:px-8" data-id="element-3090" data-mp="3kuwox7ijypjs">
                <div className="flex min-w-0 flex-1 gap-1.5" aria-label="Benefit progress" data-id="element-3091" data-mp="3pt7w1ahgqlyb">
                  {benefits.map((benefit, index) => <button key={benefit.title} type="button" onClick={() => {
                setWhyDir(index > whyIdx ? 1 : -1);
                setWhyIdx(index);
              }} aria-label={`Show benefit ${index + 1}: ${benefit.title}`} aria-current={index === whyIdx ? 'true' : undefined} className={`h-1.5 flex-1 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] ${index === whyIdx ? 'bg-[#04A05C]' : 'bg-stone-200 hover:bg-stone-300 dark:bg-white/10 dark:hover:bg-white/20'}`} data-id="element-3092" data-mp="3fwv70pwv4w9w" data-mpi={index} />)}
                </div>
                <div className="flex shrink-0 items-center gap-2" data-id="element-3093" data-mp="1f9a17rrljr12">
                  <button type="button" onClick={() => go(-1)} aria-label="Previous benefit" className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 text-brand-charcoal transition-colors hover:border-[#04A05C] hover:text-[#05894e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:text-white dark:hover:border-[#4ed491] dark:hover:text-[#4ed491]" data-id="element-3094" data-mp="1aagpo4vvea3j">
                    <ArrowLeft className="h-4 w-4" data-id="element-3095" data-mp="1szn9xwcbb9rg" />
                  </button>
                  <button type="button" onClick={() => go(1)} aria-label="Next benefit" className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 text-brand-charcoal transition-colors hover:border-[#04A05C] hover:text-[#05894e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:text-white dark:hover:border-[#4ed491] dark:hover:text-[#4ed491]" data-id="element-3096" data-mp="26ywq4w0iq3vf">
                    <ArrowRight className="h-4 w-4" data-id="element-3097" data-mp="27qms8mpjiyw5" />
                  </button>
                </div>
              </div>
            </motion.div>;
      })()}
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-stone-100 dark:bg-[#1b1b1b] border-t border-stone-200 dark:border-white/5" data-id="element-3098" data-mp="2hloffd099qw3">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3099" data-mp="2mnnliundr1wq">
          <div className="text-center mb-12" data-id="element-3100" data-mp="2qmwdbtyv0ppn">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-3101" data-mp="2qluydeeq3j2n">
              {t('lvn.faq.title')}
            </h2>
            <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-3102" data-mp="30e95kmoklhw7"></div>
          </div>

          <div className="grid gap-x-12 gap-y-10 md:grid-cols-2" data-id="element-3103" data-mp="35can336cl95k">
            <FaqItem question={t('lvn.faq.1.q')} answer={t('lvn.faq.1.a')} data-id="element-3104" data-mp="3acgh2q03b7qt" />
            <FaqItem question={t('lvn.faq.2.q')} answer={t('lvn.faq.2.a')} data-id="element-3105" data-mp="3ediaurb8mrle" />
            <FaqItem question={t('lvn.faq.3.q')} answer={t('lvn.faq.3.a')} data-id="element-3106" data-mp="3ebfmebpqh6is" />
            <FaqItem question={t('lvn.faq.4.q')} answer={t('lvn.faq.4.a')} data-id="element-3107" data-mp="12rq6i99ukmyy" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3108" data-mp="tpgsvaja8dhc">
        <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-3109" data-mp="oqdrq16faren">
          {t('lvn.cta.title')}
        </h2>
        <p className="text-xl text-stone-600 dark:text-stone-300 mb-10 max-w-2xl mx-auto font-sub" data-id="element-3110" data-mp="1mdmj2ct3uuok">
          {t('lvn.cta.body')}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3111" data-mp="1cocmokr8apun">
          <Link to="/#materials" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-3112" data-mp="1cm9q1hvklygy">
            {t('lvn.cta.btnQuote')}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3113" data-mp="20d5cs0l663hc" />
          </Link>
          <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-8 py-4 bg-stone-200 dark:bg-white/10 hover:bg-stone-300 dark:hover:bg-white/20 text-brand-charcoal dark:text-white rounded-xl font-bold text-lg transition-colors" data-id="element-3114" data-mp="18qm1pmwi5blo">
            <Phone className="w-5 h-5 mr-2" data-id="element-3115" data-mp="zrikkogwb69t" />
            {t('lvn.cta.btnCall')}
          </a>
        </div>
      </section>
    </div>;
}